import type { Component } from "schemas/component";
import type { SchemaReploShopifyProduct } from "schemas/generated/product";

import React, { useState } from "react";

import Separator from "@editor/components/common/designSystem/Separator";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { isFeatureEnabled } from "@editor/infra/featureFlags";
import { useAIStreaming } from "@editor/providers/AIStreamingProvider";
import {
  selectComponentData,
  selectDraftComponents,
  selectRootComponent,
  selectStreamingUpdate,
  setEditorMode,
  setStreamingUpdate,
} from "@editor/reducers/core-reducer";
import {
  useEditorDispatch,
  useEditorSelector,
  useEditorStore,
} from "@editor/store";
import { EditorMode } from "@editor/types/core-state";
import {
  getComponentAlignment,
  getComponentPositioning,
  getIconAndMessage,
} from "@editor/utils/tree-utils";

import { selectActiveCanvas } from "@/features/canvas/canvas-reducer";
import IconButton from "@replo/design-system/components/button/IconButton";
import Chip from "@replo/design-system/components/chip/SelectableChip";
import { BadgeCheck } from "lucide-react";
import { animated, config, useTransition } from "react-spring";
import { editorCanvasToMediaSize } from "replo-runtime/shared/utils/breakpoints";

import BuildAssistantGenerationLoader from "./BuildAssistantGenerationLoader";
import { ExpandedBuildAssistantPrompt } from "./ExpandedBuildAssistantPrompt";
import { MinimizedBuildAssistantPrompt } from "./MinimizedBuildAssistantPrompt";

interface BuildAssistantProps {
  isMinimized: boolean;
}

export const BuildAssistant: React.FC<BuildAssistantProps> = ({
  isMinimized,
}) => {
  const [prompt, setPrompt] = useState("");
  const [selectedProduct, setSelectedProduct] =
    useState<SchemaReploShopifyProduct | null>();
  const [showGenerationDone, setShowGenerationDone] = useState(false);
  const [fadeTransition, setFadeTransition] = useState(false);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const dispatch = useEditorDispatch();
  const store = useEditorStore();
  const logEvent = useLogAnalytics();
  const { abort, initiateParallelGeneration, applyChanges, isGenerating } =
    useAIStreaming();

  const draftComponents = useEditorSelector(selectDraftComponents);
  const rootComponent = selectRootComponent(store.getState());

  let targetComponents: Component[] = [];
  if (draftComponents.length > 0) {
    targetComponents = draftComponents;
  } else if (rootComponent) {
    targetComponents = [rootComponent];
  }

  const handleBuild = () => {
    if (!prompt) {
      return;
    }

    logEvent("ai.action.submitted", {
      prompt,
      triggeringFeature: "build-assistant",
    });

    if (!rootComponent) {
      return;
    }

    const componentsToGenerate = targetComponents;

    initiateParallelGeneration(
      {
        type: "template",
        userPrompt: prompt,
        nonce: 0,
        components: componentsToGenerate,
        selectedProduct,
        triggeringFeature: "build-assistant",
      },
      () => {
        onBuildComplete();
      },
    );
  };

  const handleAbort = () => {
    logEvent("ai.action.aborted", {
      triggeringFeature: "build-assistant",
    });
    abort();
  };

  const onBuildComplete = () => {
    setPrompt("");
    dispatch(setEditorMode(EditorMode.edit));

    const streamingUpdate = selectStreamingUpdate(store.getState());

    if (streamingUpdate?.id) {
      logEvent("ai.action.approved", {
        streamingUpdateId: streamingUpdate?.id,
        model: isFeatureEnabled("ai-claude")
          ? "claude-3-7-sonnet-20250219"
          : "gpt-4o",
        triggeringFeature: "build-assistant",
      });
    }

    dispatch(setStreamingUpdate(streamingUpdate));
    applyChanges();
    dispatch(setStreamingUpdate(null));

    setShowGenerationDone(true);
    setFadeTransition(true);

    setTimeout(() => {
      setFadeTransition(false);
      setTimeout(() => {
        setShowGenerationDone(false);
        setTimeout(() => {
          if (isMinimized) {
            inputRef.current?.focus();
          } else {
            textareaRef.current?.focus();
          }
        }, 100);
      }, 500);
    }, 1000);
  };

  const doneTransition = useTransition(fadeTransition, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: config.gentle,
  });

  const buildAssistantGenerating = (
    <BuildAssistantGenerationLoader handleAbort={handleAbort} />
  );

  const doneStateContent = doneTransition(
    (styles, item) =>
      item && (
        <animated.div
          style={styles}
          className="flex w-full items-center gap-2 p-3"
        >
          <IconButton
            variant="secondary"
            size="sm"
            icon={<BadgeCheck className="text-success" size={12} />}
            UNSAFE_className="bg-success-soft border-0.5 border-success/10 hover:bg-success-soft cursor-default"
          />
          <span className="text-sm font-medium">Done!</span>
        </animated.div>
      ),
  );

  const minimizedBuildAssistant = (
    <MinimizedBuildAssistantPrompt
      prompt={prompt}
      setPrompt={setPrompt}
      placeholderText="Ask for any edits or customizations..."
      onSubmit={handleBuild}
      isGenerating={isGenerating}
      inputRef={inputRef}
    />
  );

  const expandedBuildAssistant = (
    <>
      <div className="flex items-start justify-between gap-1 p-3">
        <div className="flex flex-wrap items-center gap-1">
          {targetComponents.map((component) => {
            const activeCanvas = selectActiveCanvas(store.getState());
            const componentPosition = getComponentPositioning(
              editorCanvasToMediaSize[activeCanvas].mediaSize,
              component ?? undefined,
            );
            const componentHasActions = Boolean(
              component?.props?.onClick?.length ||
                component?.props?.onHover?.length,
            );
            const componentAlignment = getComponentAlignment(
              editorCanvasToMediaSize[activeCanvas].mediaSize,
              component ?? undefined,
            );
            const isPageRoot = component.id === rootComponent?.id;

            const { icon } = getIconAndMessage(
              isPageRoot,
              component.props.direction,
              component.type,
              component.props.iconName,
              componentPosition,
              componentHasActions,
              component.name ?? "",
              componentAlignment,
              null,
            );

            const componentData = selectComponentData(
              store.getState(),
              component.id,
            );
            const label = isPageRoot
              ? "Page"
              : componentData?.label || component.name || component.type;

            return (
              <Chip
                key={component.id}
                size="sm"
                active={false}
                UNSAFE_className="hover:bg-transparent cursor-default"
                layoutClassName="max-w-80"
                startEnhancer={icon}
              >
                {label}
              </Chip>
            );
          })}
        </div>
      </div>

      <Separator orientation="horizontal" />

      <ExpandedBuildAssistantPrompt
        prompt={prompt}
        setPrompt={setPrompt}
        placeholderText="Ask for any edits or customizations..."
        selectedProduct={selectedProduct ?? null}
        setSelectedProduct={setSelectedProduct}
        onSubmit={handleBuild}
        showBrandContextPreview={false}
        showFontSample={false}
        colorOverlapSize="large"
        isGenerating={isGenerating}
        inputRef={textareaRef}
      />
    </>
  );

  let buildAssistantContent;
  if (isGenerating) {
    buildAssistantContent = buildAssistantGenerating;
  } else if (showGenerationDone) {
    buildAssistantContent = doneStateContent;
  } else if (isMinimized) {
    buildAssistantContent = minimizedBuildAssistant;
  } else {
    buildAssistantContent = expandedBuildAssistant;
  }

  return buildAssistantContent;
};
